/**
 * Field Category Mapping
 * 
 * This file provides a mapping of static fields to their appropriate categories
 * to ensure consistent categorization across the application.
 */

import { FIELD_CATEGORIES } from './fieldCategoryUtils';

/**
 * Maps static field names to their appropriate categories
 * This helps ensure consistent categorization across the application
 */
export const STATIC_FIELD_CATEGORY_MAP = {
  // Personal Information Fields
  firstNameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  middleNameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  lastNameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  firstNameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  middleNameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  lastNameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  nameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  nameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  dob: FIELD_CATEGORIES.PERSONAL_INFO,
  gender: FIELD_CATEGORIES.PERSONAL_INFO,
  nationality: FIELD_CATEGORIES.PERSONAL_INFO,
  passportNumber: FIELD_CATEGORIES.PERSONAL_INFO,
  passportIssue: FIELD_CATEGORIES.PERSONAL_INFO,
  passportExpiry: FIELD_CATEGORIES.PERSONAL_INFO,
  passportIssuePlaceEn: FIELD_CATEGORIES.PERSONAL_INFO,
  passportIssuePlaceAr: FIELD_CATEGORIES.PERSONAL_INFO,
  birthPlaceEn: FIELD_CATEGORIES.PERSONAL_INFO,
  birthPlaceAr: FIELD_CATEGORIES.PERSONAL_INFO,
  birthCertificate: FIELD_CATEGORIES.PERSONAL_INFO,
  area: FIELD_CATEGORIES.PERSONAL_INFO,

  // Identification Documents
  emiratesId: FIELD_CATEGORIES.IDENTIFICATION,
  emiratesIdIssueDate: FIELD_CATEGORIES.IDENTIFICATION,
  emiratesIdExpiryDate: FIELD_CATEGORIES.IDENTIFICATION,

  // Contact Information
  mobileNumber: FIELD_CATEGORIES.CONTACT_INFO,
  email: FIELD_CATEGORIES.CONTACT_INFO,

  // Address Details
  address: FIELD_CATEGORIES.ADDRESS,
  poBox: FIELD_CATEGORIES.ADDRESS,
  
  // Document Fields
  passportCopy: FIELD_CATEGORIES.DOCUMENTS,
  
  // Workflow-specific fields
  workflowTitle: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  workflowDescription: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  workflowPriority: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  workflowDueDate: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  assignedTo: FIELD_CATEGORIES.ASSIGNMENT,
  stepAssignment: FIELD_CATEGORIES.ASSIGNMENT,
  stepStatus: FIELD_CATEGORIES.STATUS,
  
  // Employee-specific workflow fields
  employeeId: FIELD_CATEGORIES.IDENTIFICATION,
  employeeStatus: FIELD_CATEGORIES.STATUS,
  department: FIELD_CATEGORIES.EMPLOYMENT_INFO,
  position: FIELD_CATEGORIES.EMPLOYMENT_INFO,
  joiningDate: FIELD_CATEGORIES.EMPLOYMENT_INFO,
  salary: FIELD_CATEGORIES.EMPLOYMENT_INFO,
  visaPage: FIELD_CATEGORIES.DOCUMENTS,
  labourContract: FIELD_CATEGORIES.DOCUMENTS,
  salaryCertificate: FIELD_CATEGORIES.DOCUMENTS,
  photo: FIELD_CATEGORIES.DOCUMENTS,
  shinasnama: FIELD_CATEGORIES.DOCUMENTS,
  fileNumber: FIELD_CATEGORIES.DOCUMENTS,
  resume: FIELD_CATEGORIES.DOCUMENTS,
  educationalCertificate: FIELD_CATEGORIES.DOCUMENTS,
  experienceCertificate: FIELD_CATEGORIES.DOCUMENTS,
  policeClearanceCertificate: FIELD_CATEGORIES.DOCUMENTS,
  
  // Service Specific Fields
  emirate: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  buildingNumber: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  landNumber: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  deliveryNumber: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  biometricLocation: FIELD_CATEGORIES.LOCATIONS,
  medicalLocation: FIELD_CATEGORIES.LOCATIONS,
  ohhLocation: FIELD_CATEGORIES.LOCATIONS,
};

/**
 * Helper function to get the appropriate category for a field
 * @param {string} fieldName - The name of the field
 * @returns {string} - The category code for the field
 */
export const getFieldCategory = (fieldName) => {
  if (!fieldName) return FIELD_CATEGORIES.SERVICE_SPECIFIC;
  
  // Check if we have a direct mapping for this field
  if (STATIC_FIELD_CATEGORY_MAP[fieldName]) {
    return STATIC_FIELD_CATEGORY_MAP[fieldName];
  }
  
  // For document fields, ensure they're categorized as DOCUMENTS
  const documentFieldNames = [
    'passport', 'certificate', 'visa', 'contract', 'photo', 'file', 'document',
    'upload', 'attachment', 'id', 'license', 'card'
  ];
  
  const lowerFieldName = fieldName.toLowerCase();
  if (documentFieldNames.some(keyword => lowerFieldName.includes(keyword))) {
    return FIELD_CATEGORIES.DOCUMENTS;
  }
  
  // Default to SERVICE_SPECIFIC if we can't determine the category
  return FIELD_CATEGORIES.SERVICE_SPECIFIC;
};

/**
 * Helper function to check if a field is a document field
 * @param {Object} field - The field to check
 * @returns {boolean} - True if the field is a document field
 */
export const isDocumentField = (field) => {
  if (!field) return false;
  
  // Check field type
  if (field.type === 'DOCUMENT' || field.type === 'FILE' || 
      field.type === 'PDF' || field.type === 'IMAGE') {
    return true;
  }
  
  // Check known document field names
  const documentFieldNames = [
    'passportCopy', 'visaPage', 'birthCertificate', 'salaryCertificate',
    'labourContract', 'emiratesId', 'photo', 'shinasnama',
    'resume', 'educationalCertificate', 'experienceCertificate', 'policeClearanceCertificate'
  ];
  
  if (field.name && documentFieldNames.includes(field.name)) {
    return true;
  }
  
  // Check if field name contains document-related keywords
  const documentKeywords = [
    'file', 'document', 'certificate', 'upload', 'photo', 
    'signature', 'copy', 'scan', 'passport', 'id', 'card',
    'license', 'permit', 'visa', 'contract'
  ];
  
  if (field.name && documentKeywords.some(keyword => 
    field.name.toLowerCase().includes(keyword))) {
    return true;
  }
  
  return false;
};

/**
 * Ensures consistent categorization of fields, especially document fields
 * @param {Array} fields - The fields to categorize
 * @returns {Array} - The categorized fields
 */
export const ensureConsistentFieldCategorization = (fields) => {
  if (!fields || !Array.isArray(fields)) return fields;
  
  return fields.map(field => {
    // Create a copy of the field to avoid mutating the original
    const updatedField = { ...field };
    
    // Check if this is a document field
    if (isDocumentField(field)) {
      // Force document fields to be consistently typed and categorized
      updatedField.type = 'DOCUMENT';
      updatedField.category = 'DOCUMENTS';
    } else if (field.name && STATIC_FIELD_CATEGORY_MAP[field.name]) {
      // Use our mapping for known fields
      updatedField.category = STATIC_FIELD_CATEGORY_MAP[field.name];
    }
    
    return updatedField;
  });
};

/**
 * Returns a list of static document fields that should be shown in the CUSTOMER_INFO section
 * and not duplicated in the DOCUMENTS section
 * @returns {Array} - Array of static document field names
 */
export const getStaticDocumentFields = () => {
  return [
    'birthCertificate',
    'passportCopy',
    'visaPage',
    'salaryCertificate',
    'photo',
    'shinasnama',
    'labourContract',
    'resume',
    'policeClearanceCertificate',
    'educationalCertificate',
    'experienceCertificate'
  ];
};

/**
 * Helper function to get the appropriate category for a workflow field
 * @param {string} fieldName - The name of the workflow field
 * @returns {string} - The category code for the field
 */
export const getWorkflowFieldCategory = (fieldName) => {
  // Reuse the existing getFieldCategory function
  return getFieldCategory(fieldName);
};

/**
 * Helper function to check if a field is a workflow document field
 * @param {Object} field - The field to check
 * @returns {boolean} - True if the field is a document field in workflow context
 */
/**
 * Enhanced document field detection for workflow fields
 * Uses multiple detection methods to accurately identify document fields
 * 
 * @param {Object} field - The field to check
 * @returns {boolean} - True if the field is a document field
 */
export const isWorkflowDocumentField = (field) => {
  if (!field) return false;
  
  // Check field type first (most reliable indicator)
  if (field.type === 'DOCUMENT' || field.type === 'FILE' || 
      field.type === 'PDF' || field.type === 'IMAGE') {
    return true;
  }
  
  // Get normalized field identifiers for comparison
  const fieldTitle = field.title || field.name || field.code || '';
  const fieldCode = field.code || '';
  const normalizedTitle = fieldTitle.toLowerCase().replace(/[^a-z0-9]/g, '');
  const normalizedCode = fieldCode.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  // Check against our comprehensive document field list
  const documentFields = getWorkflowDocumentFields();
  if (documentFields.includes(normalizedTitle) || documentFields.includes(normalizedCode)) {
    return true;
  }
  
  // Check for document-related patterns in field title
  const documentPatterns = [
    /upload/i, /file/i, /document/i, /photo/i, /image/i, /picture/i,
    /passport/i, /visa/i, /id\s*card/i, /certificate/i, /contract/i,
    /scan/i, /copy/i, /attachment/i, /signature/i
  ];
  
  if (documentPatterns.some(pattern => pattern.test(fieldTitle))) {
    return true;
  }
  
  // Fall back to the general document field check as a last resort
  return isDocumentField(field);
};

/**
 * Groups fields by their categories
 * @param {Array} fields - The fields to group
 * @returns {Object} - Object with category codes as keys and arrays of fields as values
 */
export const groupFieldsByCategory = (fields) => {
  if (!Array.isArray(fields) || fields.length === 0) {
    return {};
  }
  
  const groupedFields = {};
  
  // Initialize all categories
  Object.values(FIELD_CATEGORIES).forEach(category => {
    groupedFields[category] = [];
  });
  
  // Group fields by their categories
  fields.forEach(field => {
    if (!field) return;
    
    const category = field.category || FIELD_CATEGORIES.GENERAL;
    if (!groupedFields[category]) {
      groupedFields[category] = [];
    }
    
    groupedFields[category].push(field);
  });
  
  // Remove empty categories
  Object.keys(groupedFields).forEach(category => {
    if (groupedFields[category].length === 0) {
      delete groupedFields[category];
    }
  });
  
  return groupedFields;
};

/**
 * Groups workflow fields by their categories
 * @param {Array} fields - The workflow fields to group
 * @returns {Object} - Object with category codes as keys and arrays of fields as values
 */
export const groupWorkflowFieldsByCategory = (fields) => {
  // Use the implemented groupFieldsByCategory function
  return groupFieldsByCategory(fields);
};

/**
 * Returns a list of workflow-specific document fields
 * @returns {Array} - Array of workflow document field names
 */
/**
 * Returns a comprehensive list of document field names used in workflows
 * This helps with document field detection and categorization
 * 
 * @returns {Array<string>} Array of document field names (normalized to lowercase)
 */
/**
 * Returns a comprehensive list of document field names used in workflows
 * The field names are normalized by converting to lowercase and removing non-alphanumeric characters
 * This ensures consistent matching with the normalized field names in isWorkflowDocumentField
 * 
 * @returns {Array<string>} - Array of normalized document field names
 */
export const getWorkflowDocumentFields = () => {
  return [
    // ID Documents
    'passportcopy',
    'passport',
    'visapage',
    'visa',
    'visacopy',
    'emiratesid',
    'emiratesidfront',
    'emiratesidback',
    'nationalid',
    'idcard',
    'idcopy',
    'birthcertificate',
    'identitycard',
    'residencevisa',
    'workvisa',
    'workpermit',
    
    // Employment Documents
    'labourcontract',
    'employmentcontract',
    'employeecontract',
    'contract',
    'offerletter',
    'appointmentletter',
    'employmentletter',
    'salarycertificate',
    'payslip',
    'salaryslip',
    
    // Educational Documents
    'educationcertificate',
    'degree',
    'diploma',
    'certificate',
    'qualification',
    
    // Medical Documents
    'medicalreport',
    'medicalcertificate',
    'healthcertificate',
    'fitnessreport',
    
    // Photos and Images
    'photo',
    'picture',
    'image',
    'photograph',
    'employeephoto',
    'profilepicture',
    
    // Other Common Documents
    'signature',
    'signeddocument',
    'cv',
    'resume',
    'attachment',
    'document',
    'file',
    'upload',
    'scan'
  ].map(field => field.toLowerCase().replace(/[^a-z0-9]/g, ''));
};
